import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { replayAssistantUiAction } from '@/api/assistant/assistantApi';
import type { AssistantUiAction } from '@/api/assistant/assistantApi.types';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { showErrorNotification } from '@/utils/notifications';
import { useAssistantPanel } from './assistantPanelContext';
import { isSmallScreen } from './mediaQueries';
import {
  useAssistantUiActionExecutor,
  type AssistantUiActionOutcome,
} from './uiActions/useAssistantUiActionExecutor';

interface AssistantActionChipsProps {
  actions: AssistantUiAction[];
  messageId: string | null;
}

export function AssistantActionChips({ actions, messageId }: AssistantActionChipsProps) {
  const { t } = useTranslation('assistant');
  const executeAction = useAssistantUiActionExecutor();
  const { close } = useAssistantPanel();
  const [runningActionId, setRunningActionId] = useState<string | null>(null);
  const [actionAwaitingLeave, setActionAwaitingLeave] = useState<AssistantUiAction | null>(null);
  if (actions.length === 0) {
    return null;
  }

  function reactToOutcome(action: AssistantUiAction, outcome: AssistantUiActionOutcome) {
    if (outcome === 'unsaved_changes') {
      setActionAwaitingLeave(action);
      return;
    }
    if (outcome === 'target_missing') {
      showErrorNotification(t('actions.targetMissing'));
      return;
    }
    if (outcome === 'performed' && isSmallScreen()) {
      close();
    }
  }

  async function runAction(action: AssistantUiAction, allowLeavingUnsavedWork: boolean) {
    setRunningActionId(action.actionId);
    try {
      const freshAction =
        messageId === null ? action : await replayAssistantUiAction(messageId, action.actionId);
      const outcome = await executeAction(freshAction, {
        isRequestedByUser: true,
        allowLeavingUnsavedWork,
      });
      reactToOutcome(freshAction, outcome);
    } catch (replayError) {
      void replayError;
      showErrorNotification(t('actions.unavailable'));
    } finally {
      setRunningActionId(null);
    }
  }

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {actions.map(function renderChip(action) {
        const isRunning = runningActionId === action.actionId;
        return (
          <button
            key={action.actionId}
            type="button"
            disabled={isRunning}
            onClick={function handleClick() {
              void runAction(action, false);
            }}
            className="px-3 py-1.5 text-xs rounded-lg font-semibold bg-zoovpalelavender text-zooprimary hover:bg-zoopalelavender flex items-center gap-1.5 disabled:opacity-60"
          >
            {isRunning ? (
              <LoadingSpinner className="w-3 h-3" />
            ) : (
              <Icon name={action.kind === 'highlight' ? 'ScanEye' : 'MousePointerClick'} />
            )}
            {t(action.kind === 'highlight' ? 'actions.showMe' : 'actions.goThere')}
            <span className="text-zooviolet font-medium">
              ·{' '}
              {t('destinations.' + action.destination, {
                defaultValue: t('destinations.fallback'),
              })}
            </span>
          </button>
        );
      })}
      {actionAwaitingLeave === null ? null : (
        <ConfirmDialog
          title={t('actions.unsavedTitle')}
          description={t('actions.unsavedDescription')}
          confirmLabel={t('actions.leaveAnyway')}
          cancelLabel={t('actions.stay')}
          iconName="AlertTriangle"
          onConfirm={function leaveAnyway() {
            const action = actionAwaitingLeave;
            setActionAwaitingLeave(null);
            void runAction(action, true);
          }}
          onCancel={function stayOnPage() {
            setActionAwaitingLeave(null);
          }}
        />
      )}
    </div>
  );
}
